import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import type { CourseraTierSubscriptions_EndRazorpaySubscriptionForCourseraTierProductInput as EndRazorpaySubscriptionForCourseraTierProductInput } from '__generated__/graphql-types';
import type { CourseraTierSubscriptions_EndRazorpaySubscriptionForCourseraTierProductResult as EndRazorpaySubscriptionResult } from '__generated__/graphql-types-do-not-use';
import URI from 'jsuri';
import PropTypes from 'prop-types';
import { compose, mapProps } from 'recompose';

import logger from 'js/app/loggerSingleton';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import useRouter from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { VisuallyHidden } from '@coursera/cds-core';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';
import { API_BEFORE_SEND, API_ERROR, API_IN_PROGRESS } from '@coursera/coursera-ui/lib/constants/sharedConstants';
import type { ApiStatus } from '@coursera/coursera-ui/lib/constants/sharedConstants';

import useIsLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication/shared/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import PromoWarningModal from 'bundles/coursera-plus-landing-page/components/PromoWarningModal';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import CourseraPlusEnrollButtonWithContentful from 'bundles/coursera-plus-landing-page/components/enrollButtonContentful/EnrollButton';
import { HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE } from 'bundles/coursera-plus-landing-page/contentful/constants';
import type { SubscriptionTiersProductPrice } from 'bundles/coursera-plus-landing-page/contentfulData/LandingPageDataProvider';
import { useLandingPageExtendedMaybe } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { useCourseraPlusEventing } from 'bundles/coursera-plus-landing-page/utils/eventingUtils';
import { productPriceToMonetaryAmount } from 'bundles/coursera-plus-landing-page/utils/productPriceToMonetaryAmount';
import { useCourseraPlusAnnualPricePresentation } from 'bundles/coursera-plus-landing-page/utils/useCourseraPlusAnnualPricePresentation';
import type { PropsFromUseCourseraPlusAnnualPricePresentation } from 'bundles/coursera-plus-landing-page/utils/useCourseraPlusAnnualPricePresentation';
import { useImpressionForCourseraPlusPromoRedesign } from 'bundles/coursera-plus-landing-page/utils/useIsRedesignPromoPageEnabled';
import { useProductVariantUrlAction } from 'bundles/coursera-plus-landing-page/utils/useProductVariantUrlAction';
import EndRazorpaySubscriptionForCourseraTierProduct from 'bundles/coursera-plus/api/EndRazorpaySubscriptionForCourseraTierProduct.graphql';
import { ENROLL_BUTTON_TESTID, FOOTER_CTA, MONTHLY_SUBSCRIPTION_CTA } from 'bundles/coursera-plus/components/constants';
import IdealUpgradeEnrollModal from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModal';
import {
  isAnnualByVariantId,
  isMonthlyByVariantId,
  isMonthlyWithFreeTrial,
} from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import { getIsValidBundleId } from 'bundles/coursera-plus/utils/bundleUtils';
import { constants } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import {
  getIsLearnerImpressedInIndiaTiersEpic,
  setIndiaTiersUpgradeData,
} from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import type { PropsFromWithExistingCourseraLiteSubscription } from 'bundles/coursera-plus/utils/withExistingCourseraLiteSubscription';
import EnrollmentChoiceTypes from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import type { SubmitEnrollmentPromiseOptions } from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import EnrollErrorModal from 'bundles/enroll/components/common/EnrollErrorModal';
import { ENROLL } from 'bundles/enroll/utils/enrollActionParams';
import type { ApiError } from 'bundles/enroll/utils/errorUtils';
import { useNaptime } from 'bundles/naptimejs';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import SubscriptionTrialsV1 from 'bundles/naptimejs/resources/subscriptionTrials.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import type { ProductPrices } from 'bundles/payments-common/hooks/useGetProductPrices';
import { useGetProductPrices } from 'bundles/payments-common/hooks/useGetProductPrices';
import ProductType from 'bundles/payments/common/ProductType';
import ApiButton from 'bundles/red-shibas-cds/components/ApiButton';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import BillingCycleType from 'bundles/subscriptions/common/BillingCycleType';
import { SIGNUP_FORM_KEY } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/coursera-plus-landing-page';

const { COURSERA_PLUS_SUBSCRIPTION_UNDERLYING_PRODUCT_ITEM_ID } = constants;

const TrackedEnrollButton = withSingleTracked({ type: 'BUTTON' })(ApiButton);

// !important needed to override CUI inline styles applied in base component
const styles = StyleSheet.create({
  courseraPlusMonthlyLink: {
    color: '#014CA9',
  },
  button: {
    border: '0 !important',
    borderRadius: '3px !important',
    fontFamily: 'OpenSans !important',
    fontWeight: 'bold !important',
    ':disabled': {
      background: '#e5e5e5 !important',
    },
  },
  buttonSizing: {
    fontSize: '20px !important',
    lineHeight: '20px !important',
    padding: '26px 18px !important',
    [`@media (max-width: ${breakPoint.lg - 1}px)`]: {
      fontSize: '14px !important',
      lineHeight: '14px !important',
      padding: '17px 13px !important',
    },
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '17px 40px !important',
    },
  },
});

type EndRazorpaySubscriptionInput = {
  input: EndRazorpaySubscriptionForCourseraTierProductInput;
};

export type PropsForEnrollPresentation = {
  price: JSX.Element | null;
  apiStatus: ApiStatus;
  trackingName: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  rootClassName?: string;
  label?: JSX.Element | string;
  isDefaultLandingPage?: boolean;
  courseraPlusAnnualPricePresetationData?: PropsFromUseCourseraPlusAnnualPricePresentation;
} & PropsFromCourseraPlusProductOwnerships;

export type PropsFromCaller = {
  renderAsMonthlySubscriptionLink?: boolean;
  trackingName: string;
  rootClassName?: string;
  // Only one component per page should check action params to prevent duplicate componentDidMount API calls
  shouldCheckActionParams?: boolean;
  productItemId: SubscriptionTiersProductVariant;
  promoCode?: string;
  PresentationComponent?: React.ComponentType<PropsForEnrollPresentation>;
  labelFunction?: (isFreeTrialAvailable: boolean) => string;
  label?: JSX.Element | string;
  userHasCourseraPlusFreeTrial?: boolean;
  bundleId?: string | null;
} & PropsFromWithExistingCourseraLiteSubscription;

type PropsFromRouter = {
  router: InjectedRouter;
};

type PropsFromNaptime = {
  courseraPlusPrices?: ProductPrices[];
  subscriptionTrials: Array<SubscriptionTrialsV1>;
};

type PropsFromNaptimeAfterMapping = {
  courseraPlusPrice?: ProductPrices;
  subscriptionTrials: Array<SubscriptionTrialsV1>;
};

type PropsToComponentRaw = PropsFromCaller &
  PropsFromRouter &
  PropsFromNaptimeAfterMapping &
  PropsFromCourseraPlusProductOwnerships;

export type PropsToComponent = PropsFromCaller &
  PropsFromRouter &
  PropsFromCourseraPlusProductOwnerships & {
    productPrice?: SubscriptionTiersProductPrice;
  };

export const CourseraPlusEnrollButtonContent = (props: PropsToComponent) => {
  const {
    rootClassName,
    renderAsMonthlySubscriptionLink,
    trackingName,
    ownsCourseraPlus,
    ownsCourseraLite,
    productPrice,
    userHasCourseraPlusFreeTrial,
    productItemId,
    PresentationComponent,
    labelFunction,
    label,
    router,
    shouldCheckActionParams,
    bundleId,
    promoCode,
    existingCourseraLiteSubscription,
  } = props;
  const isLoggedInUser = user.isAuthenticatedUser();
  const { action, variant, selectedVariant, ...queryRest } = useProductVariantUrlAction();
  const [apiStatus, setApiStatus] = useState<ApiStatus>(API_BEFORE_SEND);
  const [error, setError] = useState<ApiError | undefined>();
  const [idealUpgradeModalOpen, setIdealUpgradeModalOpen] = useState(false);
  const [notEligibleMessage, setNotEligibleMessage] = useState<string | undefined>();
  const [redirectUrlIfNotEligible, setRedirectUrlIfNotEligible] = useState<string | undefined>();
  const liteSubscriptionId = existingCourseraLiteSubscription?.id;
  const [endRazorpaySubscriptionForCourseraTierProduct, _] = useMutation<
    EndRazorpaySubscriptionResult,
    EndRazorpaySubscriptionInput
  >(EndRazorpaySubscriptionForCourseraTierProduct);
  const { trackCta } = useCourseraPlusEventing();
  const isLegalConsolidationFlowAndHasTouQueryParam = useIsLegalConsolidationFlowAndHasTouQueryParam();
  const { generateImpressionForCourseraPlusPromoRedesign } = useImpressionForCourseraPlusPromoRedesign();
  const courseraPlusAnnualPricePresetationData = useCourseraPlusAnnualPricePresentation();

  const handleError = (apiError?: ApiError) => {
    setError(apiError);
    setApiStatus(API_ERROR);
  };

  const handleErrorModalClose = () => {
    setApiStatus(API_BEFORE_SEND);
    setError(undefined);
  };

  const checkoutSubscription = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (variant: SubscriptionTiersProductVariant) => {
      const isUpgradingFromLiteToPlusInIndia = ownsCourseraLite && getIsLearnerImpressedInIndiaTiersEpic();

      if (apiStatus === API_IN_PROGRESS) {
        return;
      }

      setApiStatus(API_IN_PROGRESS);

      if (isUpgradingFromLiteToPlusInIndia) {
        endRazorpaySubscriptionForCourseraTierProduct({
          variables: {
            input: { subscriptionId: liteSubscriptionId },
          },
          context: { clientName: 'gatewayGql' },
        })
          .then(() => {
            setIndiaTiersUpgradeData({ liteSubscriptionId });
            const handleSubmitPromise =
              choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.SUBSCRIBE_TO_COURSERA_PLUS];
            const options: SubmitEnrollmentPromiseOptions = {
              productItemId: variant,
              isSubscriptionOnly: true,
            };
            submitEnrollmentPromise({ handleSubmitPromise, options });
          })
          .catch((_error) => logger.error(_error));
      } else if (ownsCourseraLite) {
        setIdealUpgradeModalOpen(true);
        setApiStatus('API_BEFORE_SEND');
      } else {
        // We need to provide a bundleId only if we're viewing a landing page.
        // getIsValidBundleId checks that it's a bundle landing page before passing bundleId
        const bundleIdProps = bundleId && getIsValidBundleId(bundleId) ? { bundleId } : {};
        const handleSubmitPromise = choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.SUBSCRIBE_TO_COURSERA_PLUS];
        const options: SubmitEnrollmentPromiseOptions = {
          isSubscriptionOnly: true,
          productItemId: variant,
          ...bundleIdProps,
        };
        submitEnrollmentPromise({ handleSubmitPromise, options, promoCode }).catch(handleError);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiStatus, ownsCourseraLite, promoCode, liteSubscriptionId]
  );

  useEffect(() => {
    // the selectedVariant query param is used in the price comparison table,
    // so we're excluding it in this check to avoid a conflict
    if (
      shouldCheckActionParams &&
      user.isAuthenticatedUser() &&
      action === ENROLL &&
      !isLegalConsolidationFlowAndHasTouQueryParam &&
      variant &&
      // checkout redirect for selectedVariant is handled in the
      // All Plans comparison table so we are excluding the selectedVariant here
      !selectedVariant &&
      !ownsCourseraPlus
    ) {
      // Remove the variant param and replace browser history to prevent checkout redirect
      // from happening again if user decides to press the back button from checkout
      router.replace({
        pathname: router.location.pathname,
        query: { ...queryRest, action: ENROLL },
      });
      checkoutSubscription(variant ?? productItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productItemId,
    shouldCheckActionParams,
    ownsCourseraPlus,
    isLegalConsolidationFlowAndHasTouQueryParam,
    action,
    selectedVariant,
    variant,
  ]);

  useEffect(() => {
    const isLearnerEligible = !ownsCourseraPlus && !ownsCourseraLite;
    generateImpressionForCourseraPlusPromoRedesign(isLearnerEligible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownsCourseraPlus, ownsCourseraLite]);

  const onClick = () => {
    if (!isLoggedInUser) {
      trackCta(trackingName);
      router.replace({
        pathname: router.location.pathname,
        query: {
          ...router.location.query,
          authMode: SIGNUP_FORM_KEY,
          action: ENROLL,
          variant: productItemId,
          redirectToCheckout: true,
        },
      });
    } else if (ownsCourseraPlus) {
      const redirectLink = new URI('/search').addQueryParam(IS_PART_OF_COURSERA_PLUS, true).toString();
      redirect.setLocation(redirectLink);
    } else {
      trackCta(trackingName);
      checkoutSubscription(productItemId);
    }
  };

  const renderIdealUpgradeModal = () => {
    const variant = productPrice?.productItemId as SubscriptionTiersProductVariant;
    let toTierBillingCycle;
    if (isAnnualByVariantId(variant)) {
      toTierBillingCycle = BillingCycleType.ANNUAL;
    } else if (isMonthlyByVariantId(variant)) {
      toTierBillingCycle = BillingCycleType.MONTHLY;
    } else {
      throw new Error(`Invalid Product Variant ${variant}`);
    }

    return (
      idealUpgradeModalOpen && (
        <IdealUpgradeEnrollModal
          onClose={() => setIdealUpgradeModalOpen(false)}
          courseraPlusProductPrice={productPriceToMonetaryAmount(productPrice)}
          isStandaloneCourseraPlusUpgrade={true}
          toTierBillingCycle={toTierBillingCycle}
        />
      )
    );
  };

  const productPriceDisplay: JSX.Element | null = productPrice ? (
    <ReactPriceDisplay value={productPrice.amount} currency={productPrice.currencyCode} hideCurrencyCode={true} />
  ) : null;

  if (PresentationComponent) {
    return (
      <>
        <PresentationComponent
          ownsCourseraPlus={ownsCourseraPlus}
          ownsCourseraLite={ownsCourseraLite}
          apiStatus={apiStatus}
          price={productPriceDisplay}
          trackingName={trackingName}
          onClick={onClick}
          label={label}
          isDefaultLandingPage={bundleId === HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE}
          courseraPlusAnnualPricePresetationData={courseraPlusAnnualPricePresetationData}
        />
        {notEligibleMessage && (
          <PromoWarningModal message={notEligibleMessage} redirectUrlIfNotEligible={redirectUrlIfNotEligible} />
        )}
        {renderIdealUpgradeModal()}
      </>
    );
  }

  const isPresentedAsMonthly =
    isMonthlyWithFreeTrial(productItemId) && [MONTHLY_SUBSCRIPTION_CTA, FOOTER_CTA].includes(trackingName);

  if (renderAsMonthlySubscriptionLink || isPresentedAsMonthly) {
    // Don't render if there is no price or if user already owns Coursera Plus
    if (!productPrice || ownsCourseraPlus) {
      return null;
    }
    // we need to produce an enroll button / link and some kind of price information
    let message;
    let screenreaderMessage;

    if (!userHasCourseraPlusFreeTrial) {
      message = _t('{price} / month. Cancel anytime.');
      screenreaderMessage = _t('Try Coursera Plus for {price} / month. Cancel anytime.');
    } else {
      message = _t('{price} / month with a 7-day free trial');
      screenreaderMessage = _t('Try Coursera Plus for {price} / month with a 7-day free trial.');
    }

    return (
      // isPresentedAsMonthly, not PresentationComponent
      <div>
        {renderIdealUpgradeModal()}
        <TrackedButton
          disabled={apiStatus === API_IN_PROGRESS}
          trackingName={trackingName}
          className="button-link"
          onClick={onClick}
          role="link"
        >
          <span {...css(styles.courseraPlusMonthlyLink)} aria-hidden="true">
            <FormattedMessage message={message} price={productPriceDisplay} />
          </span>
          <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
            <FormattedMessage message={screenreaderMessage} price={productPriceDisplay} />
          </VisuallyHidden>
        </TrackedButton>
        {notEligibleMessage && (
          <PromoWarningModal message={notEligibleMessage} redirectUrlIfNotEligible={redirectUrlIfNotEligible} />
        )}
      </div>
    );
  } else {
    // not  displayed as monthly
    const noOwner =
      labelFunction !== undefined ? labelFunction(Boolean(userHasCourseraPlusFreeTrial)) : _t('Try Coursera Plus');
    const buttonLabel = ownsCourseraPlus ? _t('Search Coursera Plus') : noOwner;

    return (
      <div>
        {renderIdealUpgradeModal()}
        {notEligibleMessage && (
          <PromoWarningModal message={notEligibleMessage} redirectUrlIfNotEligible={redirectUrlIfNotEligible} />
        )}
        <TrackedEnrollButton
          trackingName={trackingName}
          className={css(styles.button, rootClassName ?? styles.buttonSizing).className}
          label={buttonLabel}
          onClick={onClick}
          variant="primary"
          size="medium"
          apiStatus={apiStatus}
          data-testid={ENROLL_BUTTON_TESTID}
        />
        {apiStatus === API_ERROR && <EnrollErrorModal onClose={handleErrorModalClose} error={error} />}
      </div>
    );
  }
};

CourseraPlusEnrollButtonContent.contextTypes = {
  _eventData: PropTypes.object,
};

// This HOC is not updated to support Non Contentful components for Coursera Lite
// because we currently do not use Coursera Lite in non-contentful-powered landing page environment
const CourseraPlusEnrollButtonNoContentful = compose<
  PropsToComponent,
  PropsFromNaptime & PropsFromCaller & { router: InjectedRouter }
>(
  mapProps<PropsFromNaptimeAfterMapping, PropsFromNaptime>(({ courseraPlusPrices, ...rest }) => ({
    courseraPlusPrice: courseraPlusPrices?.[0],
    ...rest,
  })),
  withCourseraPlusProductOwnerships<PropsFromCaller & PropsFromRouter>(),
  mapProps<PropsToComponent, PropsToComponentRaw>(
    ({ ownsCourseraPlus, subscriptionTrials, courseraPlusPrice, ...rest }) => ({
      ownsCourseraPlus,
      productPrice: courseraPlusPrice,
      ...rest,
    })
  )
)(CourseraPlusEnrollButtonContent);

const useCourseraPlusPricesAndFreeTrials = ({ productItemId, PresentationComponent }: PropsFromCaller) => {
  const shouldFetchPricesAndTrials = isMonthlyWithFreeTrial(productItemId) || PresentationComponent;

  const { productPrices: courseraPlusPrices } = useGetProductPrices({
    products: [{ productType: ProductType.COURSERA_PLUS_SUBSCRIPTION, productItemId }],
    skip: !shouldFetchPricesAndTrials,
  });

  const {
    data: { subscriptionTrials },
  } = useNaptime<PropsFromNaptime>(() => {
    if (shouldFetchPricesAndTrials) {
      return {
        subscriptionTrials: isAuthenticatedUser()
          ? SubscriptionTrialsV1.finder('findByUserAndUnderlyingProduct', {
              params: {
                userProductItemId: tupleToStringKey([
                  user.get().id,
                  ProductType.COURSERA_PLUS_SUBSCRIPTION,
                  COURSERA_PLUS_SUBSCRIPTION_UNDERLYING_PRODUCT_ITEM_ID,
                ]),
              },
            })
          : [],
      };
    } else {
      return { subscriptionTrials: [] };
    }
  }, [productItemId, shouldFetchPricesAndTrials]);

  return {
    courseraPlusPrices,
    subscriptionTrials,
  };
};

const PassRouterIntoNoContentful = (props: PropsFromCaller) => {
  const router = useRouter();
  const { courseraPlusPrices, subscriptionTrials } = useCourseraPlusPricesAndFreeTrials(props);

  return (
    <CourseraPlusEnrollButtonNoContentful
      {...props}
      courseraPlusPrices={courseraPlusPrices}
      subscriptionTrials={subscriptionTrials}
      router={router}
    />
  );
};

const DeferredNoContentful = deferToClientSideRender(PassRouterIntoNoContentful);
DeferredNoContentful.displayName = 'CourseraPlusEnrollButton';

const CourseraPlusEnrollButton = (props: PropsFromCaller) => {
  const landingPageExtended = useLandingPageExtendedMaybe();
  if (landingPageExtended) {
    return <CourseraPlusEnrollButtonWithContentful {...props} />;
  }
  return <DeferredNoContentful {...props} />;
};

export default CourseraPlusEnrollButton;
